import React, {
  useEffect,
  useRef,
  useState,
  createContext,
  useContext,
} from 'react';
import {
  Close, 
  Fullscreen,
} from '@material-ui/icons';
import { useLocation, useHistory } from 'react-router-dom';
import { pathsToAvoidByAssistantIcon } from 'utils/paths';
import PropTypes from 'prop-types';
import MessagesCard from 'views/Settings/Assistant/MessagesCard';
import { OK_STATUS, STATUS } from 'views/Settings/Assistant/constants';
import Dynamicore, { SERVICES } from 'services/dynamicore';
import Form from 'views/Settings/Assistant/Form';
import { dynamicSort } from 'utils/arrays';
import { getCurrentSessionData } from 'utils/aws/cognito';
import { LinearProgress, IconButton } from '@material-ui/core';

const AssistantContext = createContext();

export const useAssistant = () => {
  return useContext(AssistantContext);
};

function AssistantWrapper({ children }) {
  const messagesEndRef = useRef(null);
  const { pathname: path } = useLocation();
  const [thread, setThread] = useState();
  const [message, setMessage] = useState('');
  const [showChat, setShowChat] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [file, setFile] = useState(null);

  const history = useHistory();

  const normalizeMessages = (messages = []) =>
    (Array.isArray(messages) ? messages : [])
      .map((item) => ({
        ...item,
        content: Array.isArray(item?.content)
          ? item.content
          : typeof item?.content === 'string'
            ? [{
              type: 'text',
              text: {
                value: item.content,
              },
            }]
            : [],
      }))
      .sort(dynamicSort('created_at'));

  const hasPendingAssistantReply = (messages = []) => {
    const lastMessage = [...messages].pop();
    return lastMessage?.role === 'user';
  };

  const contextValue = {
    setShowChat,
    message,
    setMessage,
    file,
    setFile,
    thread,
    setThread,
    isLoading,
    setIsLoading,
    sendMessage: async (replaceMessage = null) => {
      const strMessage = replaceMessage || message;
      if (!strMessage) {
        console.error('no message available');
        return;
      }
      setIsLoading(true);

      if (strMessage) {
        const messageContent = [
          {
            type: 'text',
            text: {
              value: strMessage,
            },
          },
        ];

        if (file) {
          messageContent.push({
            type: 'file',
            value: {
              name: file.name,
              url: file.url,
            },
          });
        }

        setThread((prev) => {
          const newThread = {
            ...prev,
            messages: [
              ...(prev?.messages || []),
              {
                content: messageContent,
                created_at: new Date().getTime() / 1000,
                role: 'user',
              },
              {
                content: [],
                created_at: new Date().getTime() / 1000,
                role: 'assistant',
              },
            ],
            status: STATUS.SENDING,
          };
          return newThread;
        });
      }

      try {
        await Dynamicore(SERVICES.ASSISTANT_CREATE_MESSAGE).post({
          thread_id: thread?.id,
          content: strMessage,
        });
        setFile(null);

        setThread((prev) => ({
          ...prev,
          status: 'in_progress',
        }));

        if (!replaceMessage) {
          setMessage('');
        }
      } catch (error) {
        setIsLoading(false);
        if (strMessage.trim()) {
          setThread((prev) => ({
            ...prev,
            messages: prev.messages.slice(0, -2),
            status: STATUS.ERROR,
          }));
        }
      }
    },
    async handleCleanThread() {
      try {
        await Dynamicore(SERVICES.ASSISTANT_DELETE_THREAD, {
          threadId: thread?.id,
        }).delete({}, true);
        localStorage.removeItem('thread_id');
        setThread(null);
        loadData();
      } catch (error) {
        localStorage.removeItem('thread_id');
        setThread(null);
        loadData();
      }
    },
  };

  const getMessages = async function (threadId) {
    const { values = [] } = await Dynamicore(SERVICES.ASSISTANT_READ_MESSAGE, {
      threadId,
    }).get();

    const messages = normalizeMessages(values);
    const status = hasPendingAssistantReply(messages) ? 'in_progress' : STATUS.COMPLETED;

    if (status === STATUS.COMPLETED) {
      setMessage('');
    }

    return {
      messages: messages.length ? messages : thread?.messages ?? [],
      status,
    };
  };

  const loadData = async function () {
    try {
      let data = {};
      const savedThread = localStorage.getItem('thread_id');

      if (savedThread) {
        data.id = savedThread;

        const response = await getMessages(savedThread);

        data.lastQuery = new Date();
        data.messages = response.messages;
        data.status = response.status;
      } else {
        data = await Dynamicore(SERVICES.ASSISTANT_THREAD).post();

        if (!data?.id) {
          throw new Error();
        }

        localStorage.setItem('thread_id', data?.id);

        data.lastQuery = new Date();
        data.messages = [];
        data.status = STATUS.OK;
      }

      const { userData } = getCurrentSessionData();

      data.user = {
        ...userData?.UserAttributes,
        username: userData?.Username,
      };

      const lastMessage = [...(data?.messages ?? [])].pop();
      if (lastMessage?.role === 'user') {
        data.messages.push({
          content: [],
          created_at: new Date().getTime() / 1000,
          role: 'assistant',
        });
      }

      setThread(data);
    } catch (error) {
      setThread({
        ...(thread || {}),
        error: error.message,
        lastQuery: new Date().getTime() / 1000,
        status: 'ERROR',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    let intervalId;

    const checkAndUpdateMessages = async () => {
      const savedThread = localStorage.getItem('thread_id');
      if (!savedThread) return;

      try {
        const response = await getMessages(savedThread);

        setThread((prev) => {
          if (!prev) return prev;
          if (
            prev.status === response.status &&
            JSON.stringify(prev.messages) === JSON.stringify(response.messages)
          ) {
            return prev;
          }

          if (prev.messages.length > response.messages.length) {
            return prev;
          }

          return {
            ...prev,
            messages: response.messages,
            status: response.status,
          };
        });

        if (response.status === STATUS.COMPLETED) {
          clearInterval(intervalId);
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Error checking messages:', error);
        clearInterval(intervalId);
        setIsLoading(false);
      }
    };

    if (thread?.status === STATUS.SENDING || thread?.status === 'in_progress') {
      checkAndUpdateMessages();
      intervalId = setInterval(checkAndUpdateMessages, 1000);

      return () => clearInterval(intervalId);
    }
  }, [thread?.status, thread?.id]);

  useEffect(() => {
    if (showChat && (!thread || !OK_STATUS.includes(thread?.status))) {
      loadData();
    }
    if (showChat && messagesEndRef.current) {
      const timer = setTimeout(() => {
        messagesEndRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [showChat]);

  useEffect(() => {
    if (!showChat || !messagesEndRef.current) return;

    const timer = setTimeout(() => {
      messagesEndRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }, 60);

    return () => clearTimeout(timer);
  }, [showChat, thread?.messages?.length]);

  const handleFullscreen = () => {
    history.push('/settings/assistant');
  };

  return (
    <AssistantContext.Provider value={contextValue}>
      {children}
      {!pathsToAvoidByAssistantIcon.some((p) => p === path) && (
        <>
          {showChat && (
            <div
              style={{
                position: 'absolute',
                right: '20px',
                bottom: '20px',
                padding: '10px',
                borderRadius: '10px',
                border: '1px solid #db1c1f',
                backgroundColor: 'white',
                Width: '400px',
                maxWidth: '400px',
                zIndex: 120,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '10px',
                }}
              >
                <IconButton onClick={handleFullscreen} size='small'>
                  <Fullscreen />
                </IconButton>

                <IconButton onClick={() => setShowChat(false)} size='small'>
                  <Close />
                </IconButton>
              </div>
              {!thread && <LinearProgress />}
              <MessagesCard
                messagesEndRef={messagesEndRef}
                sendMessage={contextValue.sendMessage}
                setMessage={setMessage}
                thread={thread}
              />
              <Form
                isLoading={isLoading}
                message={message}
                setMessage={setMessage}
                sendMessage={contextValue.sendMessage}
                thread={thread}
                file={file}
                setFile={setFile}
              />
            </div>
          )}
        </>
      )}
    </AssistantContext.Provider>
  );
}

export default AssistantWrapper;

AssistantWrapper.propTypes = {
  children: PropTypes.any,
};
